import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Rotina, RotinaGrupo, UltimaExecucao } from '@/services/api/rotinas';
import { UltimaExecucaoInfo } from './ResultadosDoGrupo';
import { HORA_MATERIALIZACAO, proximaData, tituloDoGrupo } from './rotinaUtils';

/**
 * Identidade da Rotina: nome, se está no ar, quando rodou, quando roda de novo e
 * qual título os broadcasts vão levar. O título aparece UMA vez porque numa
 * Rotina real os dez grupos usam o mesmo — só a hora muda.
 */
export default function RotinaCabecalho({
    rotina, grupos, tituloComum, ultimaExecucao, ocupado, onRenomear, onApagar, onAlternar, somenteLeitura,
}: {
    rotina: Rotina;
    grupos: RotinaGrupo[];
    tituloComum: boolean;
    ultimaExecucao: UltimaExecucao | null | undefined;
    ocupado: boolean;
    onRenomear: () => void;
    onApagar: () => void;
    onAlternar: () => void;
    /** Admin vendo Rotina de outro usuário: por ora só vê. */
    somenteLeitura?: boolean;
}) {
    const noAr = !!rotina.ativa;
    const primeiro = grupos[0];
    return (
        <div className="flex flex-wrap items-start justify-between gap-4 p-4">
            <div className="flex min-w-0 flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-lg font-semibold">{rotina.nome}</h2>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        noAr ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'
                    }`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {noAr ? 'No ar' : 'Desligada'}
                    </span>
                </div>

                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
                    <UltimaExecucaoInfo u={ultimaExecucao} />
                    <span className="tabular-nums text-muted-foreground">
                        Próxima:{' '}
                        <span className="font-medium text-foreground">
                            {noAr
                                ? `${proximaData(rotina.dia_semana)} às ${HORA_MATERIALIZACAO}`
                                : '— desligada, não materializa'}
                        </span>
                    </span>
                </div>

                {tituloComum && primeiro && (
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground">
                        <span>Título dos broadcasts:</span>
                        <span className="truncate font-medium text-foreground" title={primeiro.titulo_modelo}>
                            {primeiro.titulo_modelo}
                        </span>
                        {primeiro.titulo_com_data_hora !== 0 ? (
                            <>
                                <code className="rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-[11px] text-indigo-800">dd/mm</code>
                                <code className="rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-[11px] text-indigo-800">(hora)</code>
                                <span className="tabular-nums">→ ex.: {tituloDoGrupo(primeiro, rotina.dia_semana)}</span>
                            </>
                        ) : (
                            <span>— sai literal, sem data nem hora</span>
                        )}
                    </div>
                )}
            </div>

            {!somenteLeitura && (
            <div className="flex items-center gap-1.5">
                <Button variant="outline" size="icon" onClick={onRenomear} aria-label="Renomear Rotina">
                    <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={onApagar} aria-label="Apagar Rotina" className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                    variant={noAr ? 'outline' : 'default'}
                    disabled={ocupado}
                    className={noAr ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                    onClick={onAlternar}
                >
                    {ocupado && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {noAr ? 'Desligar' : 'Ligar rotina'}
                </Button>
            </div>
            )}
        </div>
    );
}
